import { useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowSquareOutIcon, CopyIcon, GlobeStandIcon, LinkBreakIcon, LockSimpleIcon } from '@phosphor-icons/react';

import { expirePortPreview, getPortPreview, portInfo, useSandboxPorts } from '@/api-models/ports';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/button/Button';
import { Spinner } from '@/ui/Spinner';
import { ApiError } from '@/utils/fetch.client';

interface Forward {
  url: string;
  /** Required to revoke the link again — Daytona expires by token, not by port. */
  token: string;
}

/**
 * Forwards, held outside React so they survive the tab unmounting.
 *
 * `SidePanel` mounts this tab conditionally, so component state alone resets on
 * every tab switch — which is what used to make forwarded ports look closed. It
 * can't simply stay mounted either: `useSandboxPorts` polls every 5s through
 * `ensure_session_sandbox_live`, so an always-mounted Ports tab would keep the
 * sandbox awake forever and defeat Daytona's 5-minute auto-stop.
 *
 * Keyed by sandbox base. Not persisted, so a reload forgets these — and with the
 * token goes the ability to close them until the signature expires.
 */
const forwards = new Map<string, Record<number, Forward>>();

export function PortsTab({ base }: { base: string }) {
  const { ports, isLoading } = useSandboxPorts(base);
  const [forwarded, setForwarded] = useState<Record<number, Forward>>(() => forwards.get(base) ?? {});
  const [forwarding, setForwarding] = useState<number | null>(null);
  const [closing, setClosing] = useState<number | null>(null);

  // The map, not React state, is the source of truth on write: forwarding and
  // then immediately switching tabs unmounts this component before the request
  // resolves, at which point `setForwarded` is a no-op and the forward would be
  // silently lost.
  const commit = (next: Record<number, Forward>) => {
    forwards.set(base, next);
    setForwarded(next);
  };

  const forwardPort = async (port: number) => {
    setForwarding(port);
    try {
      const preview = await getPortPreview(base, port);
      commit({ ...(forwards.get(base) ?? {}), [port]: { url: preview.url, token: preview.token } });
    } catch {
      toast.error(`Failed to forward port ${port}`);
    } finally {
      setForwarding(null);
    }
  };

  const closePort = async (port: number) => {
    const info = forwards.get(base)?.[port];
    if (!info) return;
    setClosing(port);
    try {
      await expirePortPreview(base, port, info.token);
    } catch (e) {
      // A 4xx means Daytona doesn't know this token — already expired or revoked
      // elsewhere — so the URL is dead and dropping it is the honest thing. Only
      // a server/network failure leaves the link genuinely live, and there the
      // row has to stay Public rather than claim otherwise.
      if (!(e instanceof ApiError) || e.status >= 500) {
        toast.error(`Failed to close port ${port}`);
        setClosing(null);
        return;
      }
    }
    const next = { ...(forwards.get(base) ?? {}) };
    delete next[port];
    commit(next);
    setClosing(null);
  };

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text);
    toast.success('Address copied');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
      <div className="mb-3 flex items-center gap-2">
        {/* Every port that's listening — not the forwarded subset, which is what
            the old "Forwarded ports" wording claimed this number was. */}
        <h3 className="text-sm font-semibold text-fg">Listening ports</h3>
        <Badge variant="neutral">{ports.length}</Badge>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner size={5} />
        </div>
      ) : ports.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm text-fg-tertiary">
          No processes are listening on a port yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-line">
          {ports.map((port) => {
            const info = forwarded[port];
            const address = info?.url ?? `localhost:${port}`;
            const meta = portInfo(port);
            return (
              <div
                key={port}
                className="flex items-center gap-3 border-b border-line-subtle px-3.5 py-3 last:border-b-0"
              >
                <span className="relative flex size-1.5 shrink-0">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-75" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-success" />
                </span>
                <span className="w-14 shrink-0 font-mono text-sm text-fg">{port}</span>
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="truncate text-xs text-fg-secondary">{meta?.label ?? '—'}</span>
                  {meta?.system && (
                    <Badge variant="neutral" size="sm" className="shrink-0">
                      system
                    </Badge>
                  )}
                </span>
                {/* Fixed width, so "Public" and "Private" occupy the same space —
                    otherwise flipping a port to forwarded nudges this row's
                    address and buttons sideways. Same reason the action group
                    below is fixed: three icon buttons are narrower than one icon
                    button plus the "Forward" text button. */}
                <span
                  className={
                    info
                      ? 'flex w-22 shrink-0 items-center justify-center gap-1 rounded-full border border-brand-border bg-brand-tint px-2 py-0.5 text-2xs font-medium text-brand-text'
                      : 'flex w-22 shrink-0 items-center justify-center gap-1 rounded-full border border-line bg-surface-3 px-2 py-0.5 text-2xs font-medium text-fg-secondary'
                  }
                >
                  {info ? <GlobeStandIcon size={11} /> : <LockSimpleIcon size={11} />}
                  {info ? 'Public' : 'Private'}
                </span>
                {/* Grows with the panel — at a fixed 176px even a short preview URL
                    was truncated to nothing useful. */}
                <span className="min-w-0 flex-2 truncate font-mono text-xs text-brand-text">{address}</span>
                <div className="flex w-26 shrink-0 items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    shape="square"
                    size={7}
                    onClick={() => copy(address)}
                    aria-label="Copy address"
                  >
                    <CopyIcon size={13} />
                  </Button>
                  {info ? (
                    <>
                      <Button
                        variant="ghost"
                        shape="square"
                        size={7}
                        onClick={() => window.open(info.url, '_blank', 'noopener,noreferrer')}
                        aria-label="Open"
                      >
                        <ArrowSquareOutIcon size={13} />
                      </Button>
                      <Button
                        variant="ghost"
                        shape="square"
                        size={7}
                        isLoading={closing === port}
                        onClick={() => closePort(port)}
                        title="Close forward — the public URL stops working"
                        aria-label="Close forward"
                      >
                        <LinkBreakIcon size={13} />
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="secondary"
                      size={7}
                      isLoading={forwarding === port}
                      onClick={() => forwardPort(port)}
                    >
                      Forward
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
